import API_URLS from '../../../../config/apiConfig';
import { apiGet } from '../../../../utils/apiUtils';
import { normalizeIdToInt, normalizePriceToFloat } from '../../../../utils/normalizers/auditTicketNormalizers';
import type { Game, GameListResponse, GameReviewListRequest } from '../../../../types/features/games';

const normalizeBoolean = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return value === true || value === 1;
};

const normalizeOptionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const normalizedValue = Number(value);

  return Number.isFinite(normalizedValue) ? normalizedValue : undefined;
};

const normalizeOptionalBoolean = (value: unknown): boolean | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  return normalizeBoolean(value);
};

const normalizeGame = (game: any): Game => {
  const normalizedGameId = normalizeIdToInt(game?.game_id);
  const normalizedCost = normalizePriceToFloat(game?.cost_per_ticket);
  const normalizedTicketsPerPack = normalizeIdToInt(game?.tickets_per_pack);

  return {
    ...game,
    game_id: (normalizedGameId ?? game?.game_id) as any,
    cost_per_ticket: (normalizedCost ?? game?.cost_per_ticket ?? 0) as any,
    tickets_per_pack: (normalizedTicketsPerPack ?? game?.tickets_per_pack ?? 0) as any,
    pending_plu_verification: normalizeBoolean(game?.pending_plu_verification),
  };
};

const normalizeGameListResponse = (response: any): GameListResponse => {
  const rawGames = Array.isArray(response) ? response : response?.data;

  if (!Array.isArray(rawGames)) {
    return {
      success: false,
      data: [],
      count: 0,
      state: null,
      error: response?.error || 'Unexpected response format',
    };
  }

  const data = rawGames.map(normalizeGame);

  if (Array.isArray(response)) {
    return {
      success: true,
      data,
      count: data.length,
      total_count: data.length,
      state: null,
    };
  }

  const totalCount = normalizeOptionalNumber(response.total_count);
  const totalPages = normalizeOptionalNumber(response.total_pages);
  const pageSize = normalizeOptionalNumber(response.page_size);
  const totalCountFromPages = totalPages !== undefined && pageSize !== undefined
    ? totalPages * pageSize
    : undefined;
  const normalizedCount = totalCount
    ?? totalCountFromPages
    ?? normalizeOptionalNumber(response.count)
    ?? data.length;

  return {
    ...response,
    success: response.success ?? true,
    data,
    count: normalizedCount,
    total_count: normalizedCount,
    total_pages: totalPages,
    page: normalizeOptionalNumber(response.page),
    page_size: pageSize,
    has_next: normalizeOptionalBoolean(response.has_next),
    has_previous: normalizeOptionalBoolean(response.has_previous),
    scope: response.scope ?? null,
    state: response.state ?? null,
  };
};

const buildGameReviewListUrl = (baseUrl: string, params: GameReviewListRequest): string => {
  const searchParams = new URLSearchParams();

  if (params.state) {
    searchParams.set('state', params.state);
  }

  if (params.page) {
    searchParams.set('page', params.page.toString());
  }

  if (params.search?.trim()) {
    searchParams.set('search', params.search.trim());
  }

  const queryString = searchParams.toString();

  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

const missingGamesService = {
  async getPluVerificationPendingGames(): Promise<GameListResponse> {
    return normalizeGameListResponse(await apiGet(API_URLS.GET_PLU_VERIFICATION_PENDING_GAMES));
  },

  async getMissingImageGames(params: GameReviewListRequest = {}): Promise<GameListResponse> {
    return normalizeGameListResponse(await apiGet(buildGameReviewListUrl(API_URLS.GET_MISSING_IMAGE_GAMES, {
      page: params.page ?? 1,
      state: params.state,
      search: params.search,
    })));
  },
};

export default missingGamesService;
